import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Pill, Plus, Trash2, RefreshCw, Activity, Clock, Edit3, X, Check, Calendar, Package } from 'lucide-react';
import { Button } from '../ui/button';
import ScorteTab from './ScorteTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Giorni della settimana per selezione
const GIORNI_SETTIMANA = [
  { id: 'lunedi', label: 'Lun', full: 'Lunedì' },
  { id: 'martedi', label: 'Mar', full: 'Martedì' },
  { id: 'mercoledi', label: 'Mer', full: 'Mercoledì' },
  { id: 'giovedi', label: 'Gio', full: 'Giovedì' },
  { id: 'venerdi', label: 'Ven', full: 'Venerdì' },
  { id: 'sabato', label: 'Sab', full: 'Sabato' },
  { id: 'domenica', label: 'Dom', full: 'Domenica' }
];

// Funzione per calcolare la fascia oraria dall'orario
const getFasciaOraria = (orario) => {
  if (!orario) return null;
  const [hours] = orario.split(':').map(Number);
  if (hours >= 0 && hours <= 12) return 'mattina';
  if (hours >= 13 && hours <= 18) return 'pomeriggio';
  if (hours >= 19 && hours <= 23) return 'sera';
  return null;
};

// Funzione per formattare la fascia oraria con badge colorato
const FasciaOrariaBadge = ({ fascia, orario }) => {
  const colors = {
    mattina: 'bg-[#FEF3C7] text-[#92400E]',
    pomeriggio: 'bg-[#DBEAFE] text-[#1E40AF]',
    sera: 'bg-[#E0E7FF] text-[#3730A3]'
  };
  
  const labels = {
    mattina: 'Mattina',
    pomeriggio: 'Pomeriggio',
    sera: 'Sera'
  };
  
  if (!fascia && !orario) return null;
  
  // Normalizza fascia a lowercase e orario a HH:MM
  const fasciaLower = fascia?.toLowerCase();
  const orarioTrimmed = orario?.slice(0, 5) || '';
  
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[fasciaLower] || 'bg-gray-100 text-gray-600'}`}>
      {orarioTrimmed && <Clock className="w-3 h-3 inline mr-1" />}
      {orarioTrimmed} {fasciaLower ? `(${labels[fasciaLower] || fasciaLower})` : ''}
    </span>
  );
};

// Componente selezione giorni
const GiorniSelector = ({ selected, onChange }) => {
  const allSelected = selected.length === 7;
  
  const toggleAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(GIORNI_SETTIMANA.map(g => g.id));
    }
  };
  
  const toggleGiorno = (giornoId) => {
    if (selected.includes(giornoId)) {
      onChange(selected.filter(g => g !== giornoId));
    } else {
      onChange([...selected, giornoId]);
    }
  };
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-[#64748B]">Giorni</label>
        <button 
          onClick={toggleAll}
          className="text-xs text-[#4A7C59] hover:underline"
        >
          {allSelected ? 'Deseleziona tutti' : 'Ogni giorno'}
        </button>
      </div>
      <div className="flex gap-1.5 flex-wrap">
        {GIORNI_SETTIMANA.map(giorno => (
          <button
            key={giorno.id}
            onClick={() => toggleGiorno(giorno.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              selected.includes(giorno.id)
                ? 'bg-[#4A7C59] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title={giorno.full}
          >
            {giorno.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const MedicinesTab = ({ famiglia, onMedicinesChange }) => {
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMedicine, setEditingMedicine] = useState(null);
  const [newMedicine, setNewMedicine] = useState({ 
    nome_farmaco: '', 
    dosaggio: '',
    unita: 'compresse',
    volte_giorno: 1,
    orari: [''],
    giorni_settimana: ['lunedi','martedi','mercoledi','giovedi','venerdi','sabato','domenica']
  });
  const [saving, setSaving] = useState(false);
  const [inlineEditingId, setInlineEditingId] = useState(null); // Track inline time edit
  
  // Sotto-tab attiva: 'programma' o 'scorte'
  const [activeSubTab, setActiveSubTab] = useState('programma');
  
  // Opzioni unità di misura
  const UNITA_OPTIONS = [
    { value: 'compresse', label: 'Compresse' },
    { value: 'capsule', label: 'Capsule' },
    { value: 'gocce', label: 'Gocce' },
    { value: 'ml', label: 'ml' },
    { value: 'mg', label: 'mg' },
    { value: 'bustine', label: 'Bustine' },
    { value: 'fiale', label: 'Fiale' },
    { value: 'cerotti', label: 'Cerotti' },
    { value: 'supposte', label: 'Supposte' },
    { value: 'inalazioni', label: 'Inalazioni' }
  ];
  
  // Opzioni volte al giorno
  const VOLTE_GIORNO_OPTIONS = [
    { value: 1, label: '1 volta al giorno' },
    { value: 2, label: '2 volte al giorno' },
    { value: 3, label: '3 volte al giorno' },
    { value: 4, label: '4 volte al giorno' }
  ];
  
  // Etichette per gli orari in base al numero di volte
  const ORARI_LABELS = {
    1: ['Orario'],
    2: ['Mattina', 'Sera'],
    3: ['Mattina', 'Pranzo', 'Sera'],
    4: ['Mattina', 'Pranzo', 'Pomeriggio', 'Sera']
  };
  
  // Aggiorna orari quando cambia volte_giorno
  const handleVolteGiornoChange = (volte) => {
    const newOrari = Array(volte).fill('').map((_, i) => newMedicine.orari[i] || '');
    setNewMedicine({ ...newMedicine, volte_giorno: volte, orari: newOrari });
  };
  
  // Aggiorna un singolo orario
  const handleOrarioChange = (index, value) => {
    const newOrari = [...newMedicine.orari];
    newOrari[index] = value;
    setNewMedicine({ ...newMedicine, orari: newOrari });
  };
  
  // Notifica il parent quando le medicine cambiano
  const updateMedicines = (newMedicines) => {
    setMedicines(newMedicines);
    if (onMedicinesChange) {
      onMedicinesChange(newMedicines);
    }
  };

  useEffect(() => {
    loadMedicines();
  }, [famiglia?.id]);

  const loadMedicines = async () => {
    if (!famiglia?.id) return;
    try {
      const response = await fetch(`${API_URL}/api/medicine/schedule/${famiglia.id}`);
      if (response.ok) {
        const data = await response.json();
        const loadedMeds = data.schedule || [];
        updateMedicines(loadedMeds);
      }
    } catch (error) {
      console.error('Error loading medicines:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newMedicine.nome_farmaco.trim()) {
      toast.error('Inserisci il nome del farmaco');
      return;
    }
    
    // Verifica che tutti gli orari siano compilati
    const orariValidi = newMedicine.orari.filter(o => o && o.trim() !== '');
    if (orariValidi.length === 0) {
      toast.error("Inserisci almeno un orario");
      return;
    }
    
    if (orariValidi.length !== newMedicine.volte_giorno) {
      toast.error(`Inserisci tutti gli ${newMedicine.volte_giorno} orari`);
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/medicine/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          famiglia_id: famiglia.id,
          nome_farmaco: newMedicine.nome_farmaco,
          dosaggio: newMedicine.dosaggio || null,
          unita: newMedicine.unita,
          volte_giorno: newMedicine.volte_giorno,
          orari: orariValidi,
          giorni_settimana: newMedicine.giorni_settimana.length > 0 ? newMedicine.giorni_settimana : null
        })
      });

      if (response.ok) {
        loadMedicines(); // Ricarica la lista
        resetForm();
        toast.success('Farmaco aggiunto!');
      }
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingMedicine) return;
    
    // Verifica che tutti gli orari siano compilati
    const orariValidi = newMedicine.orari.filter(o => o && o.trim() !== '');
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/medicine/schedule/${editingMedicine.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome_farmaco: newMedicine.nome_farmaco,
          dosaggio: newMedicine.dosaggio || null,
          tipo_unita: newMedicine.unita || null,
          orario: orariValidi[0] || null,
          giorni_settimana: newMedicine.giorni_settimana.length > 0 ? newMedicine.giorni_settimana : null
        })
      });

      if (response.ok) {
        loadMedicines();
        resetForm();
        toast.success('Farmaco aggiornato!');
      }
    } catch (error) {
      toast.error("Errore durante l'aggiornamento");
    } finally {
      setSaving(false);
    }
  };

  // Inline time update for a single medicine row
  const handleInlineTimeUpdate = async (medicineId, newOrario, originalOrario) => {
    if (!newOrario || newOrario === originalOrario) {
      setInlineEditingId(null);
      return;
    }
    
    setInlineEditingId(medicineId);
    try {
      const response = await fetch(`${API_URL}/api/medicine/schedule/${medicineId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orario: newOrario })
      });

      if (response.ok) {
        toast.success('Orario aggiornato');
        loadMedicines();
      } else {
        toast.error("Errore durante l'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore durante l'aggiornamento");
    } finally {
      setInlineEditingId(null);
    }
  };

  const handleDelete = async (medicineId) => {
    if (!confirm('Eliminare questo farmaco?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/medicine/schedule/${medicineId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        updateMedicines(medicines.filter(m => m.id !== medicineId));
        toast.success('Farmaco rimosso');
      }
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const startEdit = (medicine) => {
    setEditingMedicine(medicine);
    // Supporta sia vecchio formato (orario singolo) che nuovo (orari multipli)
    const existingOrari = medicine.orari || (medicine.orario ? [medicine.orario] : ['']);
    setNewMedicine({
      nome_farmaco: medicine.nome_farmaco || medicine.nome || '',
      dosaggio: medicine.dosaggio || '',
      unita: medicine.tipo_unita || medicine.unita || 'compresse',
      volte_giorno: existingOrari.length || 1,
      orari: existingOrari.length > 0 ? existingOrari : [''],
      giorni_settimana: medicine.giorni_settimana || ['lunedi','martedi','mercoledi','giovedi','venerdi','sabato','domenica']
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingMedicine(null);
    setNewMedicine({ 
      nome_farmaco: '', 
      dosaggio: '', 
      unita: 'compresse',
      volte_giorno: 1,
      orari: [''],
      giorni_settimana: ['lunedi','martedi','mercoledi','giovedi','venerdi','sabato','domenica']
    });
  };

  // Helper per ottenere il nome del farmaco (compatibilità)
  const getMedicineName = (medicine) => {
    return medicine.nome_farmaco || medicine.nome || 'Farmaco senza nome';
  };

  // Formatta i giorni selezionati
  const formatGiorni = (giorni) => {
    if (!giorni || giorni.length === 0 || giorni.length === 7) {
      return 'Ogni giorno';
    }
    return giorni.map(g => GIORNI_SETTIMANA.find(gs => gs.id === g)?.label || g).join(', ');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#2C3E50]">Gestione Farmaci</h2>
          <p className="text-sm text-[#64748B]">Programma e scorte dei farmaci</p>
        </div>
      </div>
      
      {/* Sotto-tab: Programma / Scorte */}
      <div className="flex gap-2 p-1 bg-[#F4F7F5] rounded-xl">
        <button
          onClick={() => setActiveSubTab('programma')}
          className={`flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 ${
            activeSubTab === 'programma'
              ? 'bg-white text-[#4A7C59] shadow-sm'
              : 'text-[#64748B] hover:text-[#2C3E50]'
          }`}
        >
          <Calendar className="w-4 h-4" />
          Programma
        </button>
        <button
          onClick={() => setActiveSubTab('scorte')}
          className={`flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 ${
            activeSubTab === 'scorte'
              ? 'bg-white text-[#4A7C59] shadow-sm'
              : 'text-[#64748B] hover:text-[#2C3E50]'
          }`}
        >
          <Package className="w-4 h-4" />
          Scorte
        </button>
      </div>
      
      {/* Contenuto sotto-tab */}
      {activeSubTab === 'scorte' ? (
        <ScorteTab famiglia={famiglia} />
      ) : (
        <>
          {/* Bottone Aggiungi (solo in tab Programma) */}
          <div className="flex justify-end">
            <Button
              onClick={() => {
                resetForm();
                setShowForm(!showForm);
              }}
              className="bg-[#4A7C59] hover:bg-[#3A6345] text-white gap-2"
              data-testid="btn-add-medicine"
            >
              <Plus className="w-4 h-4" />
              Aggiungi
            </Button>
          </div>

      {/* Add/Edit Medicine Form */}
      {showForm && (
        <div className="bg-[#F4F7F5] rounded-2xl p-5 border border-[#E5E7EB]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[#2C3E50]">
              {editingMedicine ? 'Modifica Farmaco' : 'Nuovo Farmaco'}
            </h3>
            <button onClick={resetForm} className="p-1 hover:bg-gray-200 rounded-full">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#64748B] mb-1.5">Nome Farmaco *</label>
              <input
                type="text"
                value={newMedicine.nome_farmaco}
                onChange={(e) => setNewMedicine({...newMedicine, nome_farmaco: e.target.value})}
                placeholder="es. Cardioaspirina"
                className="w-full px-4 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none"
                data-testid="input-medicine-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#64748B] mb-1.5">Dosaggio</label>
              <input
                type="text"
                value={newMedicine.dosaggio}
                onChange={(e) => setNewMedicine({...newMedicine, dosaggio: e.target.value})}
                placeholder="es. 100mg"
                className="w-full px-4 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#64748B] mb-1.5">Unità</label>
              <select
                value={newMedicine.unita}
                onChange={(e) => setNewMedicine({...newMedicine, unita: e.target.value})}
                className="w-full px-4 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none bg-white"
                data-testid="input-medicine-unit"
              >
                {UNITA_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#64748B] mb-1.5">Volte al giorno</label>
              <select
                value={newMedicine.volte_giorno}
                onChange={(e) => handleVolteGiornoChange(parseInt(e.target.value))}
                className="w-full px-4 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none bg-white"
                data-testid="input-medicine-times-per-day"
              >
                {VOLTE_GIORNO_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            {/* Orari multipli */}
            <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              {newMedicine.orari.map((orario, index) => (
                <div key={index}>
                  <label className="block text-sm font-medium text-[#64748B] mb-1.5">
                    {ORARI_LABELS[newMedicine.volte_giorno]?.[index] || `Orario ${index + 1}`} *
                  </label>
                  <input
                    type="time"
                    value={orario}
                    onChange={(e) => handleOrarioChange(index, e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none bg-white"
                    data-testid={`input-medicine-time-${index}`}
                  />
                  {orario && (
                    <p className="text-xs text-[#4A7C59] mt-1">
                      <span className="capitalize">{getFasciaOraria(orario)}</span>
                    </p>
                  )}
                </div>
              ))}
            </div>
            <div>
              <GiorniSelector 
                selected={newMedicine.giorni_settimana}
                onChange={(giorni) => setNewMedicine({...newMedicine, giorni_settimana: giorni})}
              />
            </div>
          </div>
          
          <div className="flex gap-3 mt-4">
            <Button
              onClick={editingMedicine ? handleUpdate : handleAdd}
              disabled={saving || !newMedicine.nome_farmaco.trim() || newMedicine.orari.filter(o => o && o.trim()).length === 0}
              className="bg-[#4A7C59] hover:bg-[#3A6345] text-white"
              data-testid="btn-save-medicine"
            >
              {saving ? 'Salvataggio...' : (editingMedicine ? 'Aggiorna' : 'Salva Farmaco')}
            </Button>
            <Button
              onClick={resetForm}
              variant="outline"
              className="border-[#E5E7EB]"
            >
              Annulla
            </Button>
          </div>
        </div>
      )}

      {/* Medicines List */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 text-[#4A7C59] animate-spin mx-auto mb-2" />
            <p className="text-[#64748B]">Caricamento...</p>
          </div>
        ) : medicines.length === 0 ? (
          <div className="p-8 text-center">
            <Pill className="w-12 h-12 text-[#E5E7EB] mx-auto mb-3" />
            <p className="text-[#64748B] mb-1">Nessun farmaco configurato</p>
            <p className="text-sm text-[#9CA3AF]">Aggiungi i farmaci che l'assistente dovrà somministrare</p>
          </div>
        ) : (
          <div className="divide-y divide-[#E5E7EB]">
            {medicines.map((medicine) => (
              <div key={medicine.id} className="p-4 flex items-center gap-4 hover:bg-[#F9FAFB] group">
                <div className="w-10 h-10 bg-[#D5F5E3] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Pill className="w-5 h-5 text-[#27AE60]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#2C3E50] truncate">{getMedicineName(medicine)}</p>
                  <div className="flex items-center gap-2 text-sm text-[#64748B] flex-wrap">
                    {(medicine.dosaggio || medicine.tipo_unita) && (
                      <span>
                        {medicine.dosaggio || ''}
                        {medicine.tipo_unita && ` ${medicine.tipo_unita}`}
                      </span>
                    )}
                    <div className="flex items-center gap-1">
                      <FasciaOrariaBadge 
                        fascia={medicine.fascia_oraria || getFasciaOraria(medicine.orario)} 
                        orario={medicine.orario || medicine.orario_esatto} 
                      />
                      <input
                        type="time"
                        defaultValue={(medicine.orario || medicine.orario_esatto || '').slice(0, 5)}
                        disabled={inlineEditingId === medicine.id}
                        onBlur={(e) => {
                          const newValue = e.target.value;
                          const originalValue = (medicine.orario || medicine.orario_esatto || '').slice(0, 5);
                          if (newValue && newValue !== originalValue) {
                            handleInlineTimeUpdate(medicine.id, newValue, originalValue);
                          }
                        }}
                        className="w-[5.5rem] px-1.5 py-0.5 text-xs border border-[#4A7C59]/30 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#4A7C59] disabled:opacity-50 disabled:cursor-wait"
                        title="Modifica orario"
                      />
                    </div>
                    {/* Badge giorni */}
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatGiorni(medicine.giorni_settimana)}
                    </span>
                  </div>
                </div>
                
                {/* Action buttons */}
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => startEdit(medicine)}
                    className="p-2 text-[#4A7C59] hover:bg-green-50 rounded-xl transition-colors"
                    title="Modifica"
                  >
                    <Edit3 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(medicine.id)}
                    className="p-2 text-[#EF4444] hover:bg-red-50 rounded-xl transition-colors"
                    title="Elimina"
                    data-testid={`btn-delete-${medicine.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-[#EBF5FB] rounded-2xl p-4 border border-[#5DADE2]/20">
        <div className="flex gap-3">
          <div className="w-8 h-8 bg-[#5DADE2]/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <Activity className="w-4 h-4 text-[#5DADE2]" />
          </div>
          <div>
            <p className="font-medium text-[#2471A3] text-sm">Come funziona?</p>
            <p className="text-sm text-[#5DADE2] mt-1">
              I farmaci che aggiungi qui appariranno nell'app dell'assistente <strong>solo nei giorni selezionati</strong>. 
              Questo permette di gestire terapie settimanali o a giorni alterni.
            </p>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default MedicinesTab;
